import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a product price.
 * It listens for variant updates and swaps only the price container
 * that belongs to the same product/block.
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    if (this.dataset.priceListenerAttached === 'true') return;

    this._closestContainer = this.closest('.shopify-section, dialog');
    if (!this._closestContainer) return;

    this._closestContainer.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.dataset.priceListenerAttached = 'true';
  }

  disconnectedCallback() {
    if (!this._closestContainer) return;

    this._closestContainer.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.dataset.priceListenerAttached = 'false';
  }

  /**
   * Finds the correct product-price root in the returned HTML.
   * Priority:
   * 1. same productId + same blockId
   * 2. same productId
   * 3. same blockId
   * 4. first product-price found
   *
   * @param {Document} html
   * @param {string} productId
   * @returns {HTMLElement | null}
   */
  findMatchingPriceRoot(html, productId) {
    const candidates = Array.from(html.querySelectorAll('product-price'));

    if (!candidates.length) return null;

    const currentBlockId = String(this.dataset.blockId || '');
    const currentProductId = String(productId || this.dataset.productId || '');

    if (currentProductId && currentBlockId) {
      const exactMatch = candidates.find((node) => {
        return (
          String(node.dataset.productId || '') === currentProductId &&
          String(node.dataset.blockId || '') === currentBlockId
        );
      });

      if (exactMatch) return exactMatch;
    }

    if (currentProductId) {
      const productMatch = candidates.find((node) => {
        return String(node.dataset.productId || '') === currentProductId;
      });

      if (productMatch) return productMatch;
    }

    if (currentBlockId) {
      const blockMatch = candidates.find((node) => {
        return String(node.dataset.blockId || '') === currentBlockId;
      });

      if (blockMatch) return blockMatch;
    }

    return candidates[0] || null;
  }

  /**
   * Updates the price container when the variant changes.
   * @param {Event} event
   */
  updatePrice = (event) => {
    const detail = event?.detail;
    const html = detail?.data?.html;

    if (!html) return;

    const nextProductId =
      String(detail?.data?.newProduct?.id || '') ||
      String(detail?.data?.productId || '') ||
      String(this.dataset.productId || '');

    const currentProductId = String(this.dataset.productId || '');

    /**
     * If this price block belongs to another product inside the same section,
     * ignore the event unless a new product was explicitly loaded.
     */
    if (
      !detail?.data?.newProduct &&
      nextProductId &&
      currentProductId &&
      nextProductId !== currentProductId
    ) {
      return;
    }

    if (nextProductId) {
      this.dataset.productId = nextProductId;
    }

    const newPriceRoot = this.findMatchingPriceRoot(html, nextProductId);
    const newPrice = newPriceRoot?.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!(newPrice instanceof HTMLElement) || !(currentPrice instanceof HTMLElement)) return;

    const newMarkup = newPrice.innerHTML.trim();
    const currentMarkup = currentPrice.innerHTML.trim();

    if (newMarkup !== currentMarkup) {
      currentPrice.replaceWith(newPrice.cloneNode(true));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}